
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpenseDetailsDialog } from "./ExpenseDetailsDialog";

interface PaymentExpensesListProps {
  paymentId: string;
  expenseIds: string[] | null;
}

export const PaymentExpensesList = ({ paymentId, expenseIds }: PaymentExpensesListProps) => {
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: expenses, isLoading } = useQuery({
    queryKey: ['payment-expenses', paymentId],
    queryFn: async () => {
      if (!expenseIds?.length) return [];
      
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          id,
          amount,
          description,
          expense_date,
          expense_categories (
            name
          )
        `)
        .in('id', expenseIds);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!paymentId && !!expenseIds?.length,
  });

  const handleExpenseClick = (expenseId: string) => {
    setSelectedExpenseId(expenseId);
    setIsDialogOpen(true);
  };

  if (!expenseIds?.length) {
    return null;
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-xl">Gastos asociados</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : expenses && expenses.length > 0 ? (
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {expenses.map(expense => (
              <div 
                key={expense.id} 
                className="flex items-start space-x-3 p-3 border rounded-md hover:bg-primary/30 cursor-pointer"
                onClick={() => handleExpenseClick(expense.id)}
              >
                <div className="grid gap-1.5 leading-none w-full">
                  <div className="text-sm font-medium flex justify-between w-full">
                    <span>{expense.expense_categories.name}</span>
                    <span className="font-semibold">
                      ${expense.amount.toLocaleString('es-CL')}
                    </span>
                  </div>
                  {expense.description && (
                    <p className="text-xs text-muted-foreground">
                      {expense.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No hay gastos asociados a este pago</p>
        )}
      </CardContent>

      <ExpenseDetailsDialog 
        expenseId={selectedExpenseId}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </Card>
  );
};
